import BaseApiService from '../BaseApiService.js'

/**
 * Program Service
 * Handles all program-related API operations
 */
class ProgramService extends BaseApiService {
  constructor() {
    super('programs')
  }

  /**
   * Get all programs with optional filters
   * @param {object} params - Query parameters (status, search, sort_by, sort_direction)
   * @returns {Promise} Programs list response
   */
  async getPrograms(params = {}) {
    return await this.get('', params)
  }

  /**
   * Generate the next auto-incremented program code (PRG-1, PRG-2, etc.)
   * @returns {Promise} Response containing { code: 'PRG-X' }
   */
  async generateCode() {
    return await this.get('generate-code')
  }

  /**
   * Get program statistics
   * @returns {Promise} Program statistics
   */
  async getProgramStatistics() {
    return await this.get('statistics')
  }

  /**
   * Get a specific program by ID
   * @param {string|number} id - Program ID
   * @returns {Promise} Program details response
   */
  async getProgram(id) {
    return await this.get(id)
  }

  /**
   * Create a new program
   * @param {object} programData - Program data
   * @returns {Promise} Created program response
   */
  async createProgram(programData) {
    return await this.post(programData)
  }

  /**
   * Update an existing program
   * @param {string|number} id - Program ID
   * @param {object} programData - Updated program data
   * @returns {Promise} Updated program response
   */
  async updateProgram(id, programData) {
    return await this.put(id, programData)
  }

  /**
   * Delete a program
   * @param {string|number} id - Program ID
   * @returns {Promise} Delete response
   */
  async deleteProgram(id) {
    return await this.delete(id)
  }

  /**
   * Submit a draft program for review
   * @param {string|number} id - Program ID
   * @returns {Promise} Submit response
   */
  async submitProgram(id) {
    return await this.post({}, `${id}/submit`)
  }

  /**
   * Approve Phase 1 (Program Definition)
   * @param {string|number} id - Program ID
   * @param {string} comments - Approval comments (optional)
   * @returns {Promise} Approval response
   */
  async approvePhase1(id, comments = '', classification = null) {
    return await this.post({ comments, classification }, `${id}/approve-phase1`)
  }

  /**
   * Reject Phase 1 (Program Definition)
   * @param {string|number} id - Program ID
   * @param {string} reason - Rejection reason (required)
   * @returns {Promise} Rejection response
   */
  async rejectPhase1(id, reason) {
    return await this.post({ reason }, `${id}/reject-phase1`)
  }

  /**
   * Get the program schedule hierarchy (Program → Projects → Deliverables → Tasks)
   * @param {string|number} id - Program ID
   * @returns {Promise} Schedule hierarchy response
   */
  async getSchedule(id) {
    return await this.get(`${id}/schedule`)
  }

  /**
   * Get program progress breakdown with weighted calculations
   * Hierarchy: Tasks → Deliverables → Projects → Program
   * @param {string|number} id - Program ID
   * @returns {Promise} Progress breakdown response
   */
  async getProgressBreakdown(id) {
    return await this.get(`${id}/progress-breakdown`)
  }

  /**
   * Update project weights within a program
   * @param {string|number} programId
   * @param {Array<{project_id: number, weight: number}>} weights - Must sum to 100
   */
  async updateProjectWeights(programId, weights) {
    return await this.put(programId, { weights }, 'project-weights')
  }
}

// Export a singleton instance
export default new ProgramService()
